'use client'

import { SidebarSearchField } from '@/components/ui/TextField/SidebarSearchField'
import { graphql } from '@/generated/gql'
import type {
  GmItemSearchQuery,
  GmItemSearchQueryVariables,
  VendorsInRangeQueryVariables,
} from '@/generated/gql/graphql'
import type { UserBasketSettingsLocationFragment } from '@/generated/graphql'
import { useLazyQuery } from '@apollo/client'
import { Search } from '@mui/icons-material'
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'

import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'

const gmItemSearchQuery = graphql(/* GraphQL */ `
  query GmItemSearch(
    $queryText: String!
    $tenantId: String!
    $latitude: Float!
    $longitude: Float!
    $cuisineTags: [String!]
    $dietaryTags: [String!]
    $occasionTags: [String!]
    $keyDatesTags: [String!]
    $diversityOfOwnershipTags: [String!]
    $bestForTags: [String!]
    $highlightsTags: [String!]
    $supplierType: String
    $headCount: Int
    $dateTime: GmItemSearchDateTimeInput
  ) {
    gmItemSearch(
      queryText: $queryText
      tenantId: $tenantId
      latitude: $latitude
      longitude: $longitude
      cuisineTags: $cuisineTags
      dietaryTags: $dietaryTags
      occasionTags: $occasionTags
      keyDatesTags: $keyDatesTags
      diversityOfOwnershipTags: $diversityOfOwnershipTags
      bestForTags: $bestForTags
      highlightsTags: $highlightsTags
      supplierType: $supplierType
      headCount: $headCount
      dateTime: $dateTime
    ) {
      supplierId
      supplierName
      permalink
      rank
      reason
    }
  }
`)

const normalizeTagList = (
  value:
    | VendorsInRangeQueryVariables['cuisineTags']
    | VendorsInRangeQueryVariables['dietaryTags']
    | VendorsInRangeQueryVariables['occasionTags']
    | VendorsInRangeQueryVariables['keyDatesTags']
    | VendorsInRangeQueryVariables['highlightsTags']
    | VendorsInRangeQueryVariables['diversityOfOwnershipTags']
    | VendorsInRangeQueryVariables['bestForTags']
    | undefined,
): string[] | undefined => {
  if (value == null) return undefined
  const arr = Array.isArray(value) ? value : [value]
  const out = arr.filter((v): v is string => typeof v === 'string' && v.length > 0)
  return out.length ? out : undefined
}

const gmItemSearchFilterVariablesFromVendorFilters = (
  filters: Partial<VendorsInRangeQueryVariables>,
): Omit<GmItemSearchQueryVariables, 'queryText' | 'tenantId' | 'latitude' | 'longitude'> => ({
  cuisineTags: normalizeTagList(filters.cuisineTags),
  dietaryTags: normalizeTagList(filters.dietaryTags),
  occasionTags: normalizeTagList(filters.occasionTags),
  keyDatesTags: normalizeTagList(filters.keyDatesTags),
  diversityOfOwnershipTags: normalizeTagList(filters.diversityOfOwnershipTags),
  bestForTags: normalizeTagList(filters.bestForTags),
  highlightsTags: normalizeTagList(filters.highlightsTags),
  supplierType: filters.supplierType ?? undefined,
  headCount: filters.headCount != null && filters.headCount > 0 ? filters.headCount : undefined,
  dateTime: filters.dateTime ?? undefined,
})

type Props = {
  tenantId: string
  location?: UserBasketSettingsLocationFragment
  filters: Partial<VendorsInRangeQueryVariables>
  onSearchTriggered?: (searchTerm: string) => void
  onSearchComplete?: (
    searchTerm: string,
    results: GmItemSearchQuery['gmItemSearch'],
    hasError: boolean,
  ) => void
  onSearchCleared?: () => void
  disableSidebarQuery?: boolean
  searching?: boolean
}

const getCoordinates = (location?: UserBasketSettingsLocationFragment) => {
  if (!location) return null
  if (location.__typename === 'Address') {
    const { latitude, longitude } = location
    if (latitude != null && longitude != null) return { latitude, longitude }
  }
  if (location.__typename === 'AccountLocation') {
    const { latitude, longitude } = location.address
    if (latitude != null && longitude != null) return { latitude, longitude }
  }
  return null
}

const SidebarItemSearch: React.FC<Props> = ({
  tenantId,
  location,
  filters,
  onSearchTriggered,
  onSearchComplete,
  onSearchCleared,
  disableSidebarQuery,
  searching,
}) => {
  const [searchValue, setSearchValue] = useState(() => filters.itemSearch?.trim() ?? '')
  const [searched, setSearched] = useState(false)
  const [results, setResults] = useState<GmItemSearchQuery['gmItemSearch']>([])
  const [queryError, setQueryError] = useState<Error | null>(null)
  const searchTermRef = useRef('')
  const prevNormalizedItemSearchRef = useRef<string | undefined>(undefined)

  const clearItemSearch = () => {
    setSearchValue('')
    setSearched(false)
    setResults([])
    setQueryError(null)
    searchTermRef.current = ''
  }

  useEffect(() => {
    const normalizedItemSearch = filters.itemSearch?.trim() ?? ''
    const previousItemSearch = prevNormalizedItemSearchRef.current
    const hasItemSearchBeenCleared = previousItemSearch && normalizedItemSearch === ''

    if (hasItemSearchBeenCleared) {
      clearItemSearch()
    }

    prevNormalizedItemSearchRef.current = normalizedItemSearch
  }, [filters.itemSearch])

  const [runSearch, { loading }] = useLazyQuery<GmItemSearchQuery, GmItemSearchQueryVariables>(
    gmItemSearchQuery,
    {
      fetchPolicy: 'no-cache',
    },
  )

  const handleSearch = async () => {
    const coords = getCoordinates(location)
    if (!coords || !searchValue.trim()) return

    const trimmedSearchValue = searchValue.trim()

    onSearchTriggered?.(trimmedSearchValue)
    if (disableSidebarQuery) {
      setSearched(true)
      return
    }
    searchTermRef.current = searchValue.trim()
    setSearched(true)
    setResults([])
    setQueryError(null)

    try {
      const { data } = await runSearch({
        variables: {
          queryText: trimmedSearchValue,
          tenantId,
          latitude: coords.latitude,
          longitude: coords.longitude,
          ...gmItemSearchFilterVariablesFromVendorFilters(filters),
        },
      })

      const completed = data?.gmItemSearch ?? []
      setResults(completed)
      onSearchComplete?.(searchTermRef.current, completed, false)
    } catch (error) {
      const resolvedError = error instanceof Error ? error : new Error('Item search failed')
      setQueryError(resolvedError)
      onSearchComplete?.(searchTermRef.current, [], true)
    }
  }
  const coords = getCoordinates(location)

  const mainVendorSearchLoading = !!filters.itemSearch && disableSidebarQuery && searching
  return (
    <>
      <Stack alignItems={'center'}>
        {!coords && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Please set a delivery address to use Item Search.
          </Alert>
        )}

        {queryError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Something went wrong. Please try again.
          </Alert>
        )}

        {searched && !disableSidebarQuery && (
          <Box
            sx={(theme) => ({
              borderTop: 1,
              borderLeft: 1,
              borderRight: 1,
              borderColor: 'divider',
              borderRadius: theme.shape.borderRadius,
              width: '90%',
              p: 2,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            })}>
            <Stack spacing={1}>
              {loading && (
                <Stack alignItems="center" py={1}>
                  <CircularProgress size={20} />
                </Stack>
              )}

              {!loading && !disableSidebarQuery && results.length === 0 && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Nothing found… Try a different keyword or fewer filters.
                </Typography>
              )}
              {!loading &&
                results.length > 0 &&
                results.map((item) => (
                  <Box key={item.supplierId}>
                    <Link
                      href={{
                        pathname: `/office-catering/vendors/${item.permalink}`,
                        query: { source: 'item-search', itemSearchTerm: searchTermRef.current },
                      }}
                      style={{ textDecoration: 'none' }}
                      target="_blank"
                      rel="noopener noreferrer">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary.main"
                        sx={{ '&:hover': { textDecoration: 'underline' } }}>
                        {item.supplierName}
                      </Typography>
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      {item.reason}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </Box>
        )}

        <SidebarSearchField
          id="sidebar-item-search-input"
          placeholder="Search for an item..."
          startIcon={<Search />}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value)
            if (!e.target.value) {
              setResults([])
              setQueryError(null)
              setSearched(false)
              onSearchCleared?.()
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void handleSearch()
            }
          }}
          disabled={!coords}
          fullWidth
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', alignSelf: 'flex-start', mt: 1 }}>
          Find dishes across all vendors
        </Typography>
      </Stack>
      {searchValue.trim() && (
        <Box display="flex" justifyContent="flex-end" marginTop={1}>
          <Button
            variant="outlined"
            sx={{
              borderRadius: 4,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              px: 1.2,
              py: 0.6,
              fontSize: '0.7rem',
              lineHeight: 1.2,
            }}
            onClick={() => {
              void handleSearch()
            }}
            disabled={!coords || loading || mainVendorSearchLoading}
            startIcon={mainVendorSearchLoading ? <CircularProgress size={12} /> : undefined}>
            Search
          </Button>
        </Box>
      )}
    </>
  )
}

export default SidebarItemSearch
export { SidebarItemSearch }
